import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  DetailsList,
  IColumn,
  SelectionMode,
  DetailsListLayoutMode,
} from '@fluentui/react';
import { statusverlaufApi } from '../services/api';

interface IVerlaufEvent {
  typ: string;
  datum: string | null;
  beschreibung: string;
  details: string;
  positionId: number | null;
  posNummer: string | null;
}

interface Props {
  bestellungId: number;
}

const EVENT_ICONS: Record<string, string> = {
  bestellung_erstellt:   '📋',
  status_geaendert:      '🔁',
  flow_zugeordnet:       '🔄',
  freigabe_erteilt:      '✅',
  freigabe_abgelehnt:    '❌',
  freigabe_komplett:     '✔️',
  in_bc_uebertragen:     '📤',
  vollstaendig_geliefert:'📦',
  teilweise_geliefert:   '🔸',
};

const EVENT_COLORS: Record<string, string> = {
  bestellung_erstellt:   '#0078d4',
  status_geaendert:      '#605e5c',
  flow_zugeordnet:       '#8764b8',
  freigabe_erteilt:      '#107c10',
  freigabe_abgelehnt:    '#a4262c',
  freigabe_komplett:     '#107c10',
  in_bc_uebertragen:     '#0078d4',
  vollstaendig_geliefert:'#107c10',
  teilweise_geliefert:   '#5db85d',
};

function formatDatum(iso: string | null): string {
  if (!iso) return '–';
  // Z-Suffix erzwingen falls nicht vorhanden — SQL-Server liefert DateTime ohne TZ-Info (= UTC)
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  return new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

export const StatusverlaufPanel: React.FC<Props> = ({ bestellungId }) => {
  const [events, setEvents] = useState<IVerlaufEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await statusverlaufApi.getByBestellung(bestellungId);
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      setError('Statusverlauf konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [bestellungId]);

  useEffect(() => { load(); }, [load]);

  const columns: IColumn[] = [
    {
      key: 'datum',
      name: 'Datum',
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: IVerlaufEvent) => (
        <Text variant="small" styles={{ root: { color: '#555', whiteSpace: 'nowrap' } }}>
          {formatDatum(item.datum)}
        </Text>
      ),
    },
    {
      key: 'ereignis',
      name: 'Ereignis',
      minWidth: 160,
      maxWidth: 240,
      isResizable: true,
      onRender: (item: IVerlaufEvent) => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
          <span style={{ fontSize: 14 }}>{EVENT_ICONS[item.typ] || '•'}</span>
          <Text
            variant="small"
            styles={{ root: { color: EVENT_COLORS[item.typ] || '#333', fontWeight: 600 } }}
          >
            {item.beschreibung}
          </Text>
        </Stack>
      ),
    },
    {
      key: 'details',
      name: 'Details',
      minWidth: 200,
      isResizable: true,
      onRender: (item: IVerlaufEvent) => (
        <Text variant="small" styles={{ root: { color: '#444' } }}>
          {item.details}
        </Text>
      ),
    },
    {
      key: 'position',
      name: 'Position',
      minWidth: 80,
      maxWidth: 100,
      isResizable: true,
      onRender: (item: IVerlaufEvent) =>
        item.posNummer ? (
          <Text variant="small" styles={{ root: { color: '#555' } }}>
            Pos. {item.posNummer}
          </Text>
        ) : (
          <Text variant="small" styles={{ root: { color: '#aaa' } }}>–</Text>
        ),
    },
  ];

  if (loading) return <Spinner size={SpinnerSize.medium} label="Verlauf laden…" />;

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {events.length === 0 ? (
        <Text styles={{ root: { color: '#666', fontStyle: 'italic' } }}>
          Kein Verlauf vorhanden.
        </Text>
      ) : (
        <DetailsList
          items={events}
          columns={columns}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          isHeaderVisible={true}
          styles={{
            root: {
              selectors: {
                '.ms-DetailsRow': { borderBottom: '1px solid #f0f0f0' },
                '.ms-DetailsHeader': { paddingTop: 0 },
              },
            },
          }}
        />
      )}
    </Stack>
  );
};
